import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import {
  Search as SearchIcon,
  Loader2,
  FileText,
  Building2,
  User,
  DollarSign,
  ListTodo,
  Calendar,
  Pencil,
  MessageSquareText,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toDisplayDate } from '@/lib/utils'
import {
  useGlobalSearch,
  useUsers,
  useUpdateAccount,
  useUpdateContact,
  useUpdateLead,
  useUpdateOpportunity,
} from '@/hooks/use-api'
import { useQueryClient } from '@tanstack/react-query'

interface SearchResult {
  id: string
  entity_type: string
  rank: number
  highlight: string
  [key: string]: any
}

interface EntityResults {
  results: SearchResult[]
  total: number
  hasMore: boolean
}

const ENTITY_ICONS: Record<string, any> = {
  accounts: Building2,
  contacts: User,
  leads: User,
  opportunities: DollarSign,
  tasks: ListTodo,
  events: Calendar,
  products: FileText,
  notes: MessageSquareText,
  noteSessions: MessageSquareText,
}

const ENTITY_LABELS: Record<string, string> = {
  accounts: 'Accounts',
  contacts: 'Contacts',
  leads: 'Leads',
  opportunities: 'Opportunities',
  tasks: 'Tasks',
  events: 'Events',
  products: 'Products',
  notes: 'Notes',
  noteSessions: 'Call Sessions',
}

const ENTITY_ROUTES: Record<string, string> = {
  accounts: '/accounts',
  contacts: '/contacts',
  leads: '/leads',
  opportunities: '/opportunities',
  tasks: '/tasks',
  events: '/events',
  products: '/products',
  // Notes don't have individual routes; we link to the parent entity
  // noteSessions don't have individual routes either
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialQuery = searchParams.get('q') || ''
  const [query, setQuery] = useState(initialQuery)
  const [activeTab, setActiveTab] = useState('all')
  const [editingOwner, setEditingOwner] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const { data: usersData } = useUsers({ limit: 10000 })
  const users = (usersData as any)?.data ?? []
  const salesUsers = users.filter((u: any) =>
    ['User', 'Admin', 'Super Admin'].includes(u.user_type)
  )

  const updateAccount = useUpdateAccount()
  const updateContact = useUpdateContact()
  const updateLead = useUpdateLead()
  const updateOpportunity = useUpdateOpportunity()

  const handleOwnerChange = async (entityType: string, entityId: string, newOwnerId: string) => {
    try {
      const mutation =
        entityType === 'accounts'
          ? updateAccount
          : entityType === 'contacts'
            ? updateContact
            : entityType === 'leads'
              ? updateLead
              : updateOpportunity
      await mutation.mutateAsync({ id: entityId, data: { owner_id: newOwnerId } } as any)
      queryClient.invalidateQueries({ queryKey: ['search'] })
      setEditingOwner(null)
    } catch (err) {
      console.error('Failed to update owner:', err)
    }
  }

  const { data, isLoading, error } = useGlobalSearch(initialQuery, {
    enabled: initialQuery.length >= 2,
  })

  useEffect(() => {
    const newQuery = searchParams.get('q') || ''
    if (newQuery !== query) {
      setQuery(newQuery)
    }
  }, [searchParams])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      setSearchParams({ q: query })
    }
  }

  const searchData = data as
    | {
        data?: {
          results?: Record<string, { total?: number; data?: unknown[] }>
          totalResults?: number
        }
      }
    | undefined
  const results = searchData?.data?.results || {}
  const totalResults = searchData?.data?.totalResults || 0
  const entityTypes = Object.keys(results).filter((type) => (results[type]?.total ?? 0) > 0)

  const renderResultCard = (result: SearchResult) => {
    const Icon = ENTITY_ICONS[result.entity_type]
    const entityLabel = ENTITY_LABELS[result.entity_type]
    const baseRoute = ENTITY_ROUTES[result.entity_type]

    // For notes, link to parent entity instead of a note detail page
    const isNote = result.entity_type === 'notes' || result.entity_type === 'noteSessions'
    const parentEntityId = (result as any).parent_entity_id
    const parentEntityType = (result as any).parent_entity_type
    const parentRoute =
      parentEntityType === 'Account'
        ? '/accounts'
        : parentEntityType === 'Contact'
          ? '/contacts'
          : parentEntityType === 'Lead'
            ? '/leads'
            : parentEntityType === 'Opportunity'
              ? '/opportunities'
              : null
    const resultRoute =
      isNote && parentRoute && parentEntityId
        ? `${parentRoute}/${parentEntityId}`
        : `${baseRoute}/${result.id}`

    return (
      <Card
        key={`${result.entity_type}-${result.id}`}
        className="hover:border-primary transition-colors"
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              {Icon && (
                <Icon
                  className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <CardTitle className="text-base truncate">
                    <Link
                      to={resultRoute}
                      className="hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    >
                      {result.name ||
                        result.subject ||
                        result.title ||
                        result.first_name + ' ' + result.last_name ||
                        'Untitled'}
                    </Link>
                  </CardTitle>
                  <Badge variant="secondary" className="text-xs flex-shrink-0">
                    {entityLabel}
                  </Badge>
                </div>
                {result.highlight && (
                  <CardDescription
                    className="mt-1"
                    dangerouslySetInnerHTML={{ __html: result.highlight }}
                  />
                )}
              </div>
            </div>
            <Link to={resultRoute}>
              <Button variant="outline" size="sm" aria-label={`View ${entityLabel}`}>
                View
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {result.entity_type === 'accounts' && (
              <>
                {result.industry && (
                  <>
                    <dt className="text-muted-foreground">Industry</dt>
                    <dd>{result.industry}</dd>
                  </>
                )}
                {result.billing_city && (
                  <>
                    <dt className="text-muted-foreground">Location</dt>
                    <dd>
                      {result.billing_city}, {result.billing_state}
                    </dd>
                  </>
                )}
              </>
            )}
            {result.entity_type === 'contacts' && (
              <>
                {result.email && (
                  <>
                    <dt className="text-muted-foreground">Email</dt>
                    <dd className="truncate">{result.email}</dd>
                  </>
                )}
                {result.account_name && (
                  <>
                    <dt className="text-muted-foreground">Account</dt>
                    <dd>
                      {result.account_id && result.account_name ? (
                        <Link
                          to={`/accounts/${result.account_id}`}
                          className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                          aria-label={`View account: ${result.account_name}`}
                        >
                          {result.account_name}
                        </Link>
                      ) : (
                        <span>{result.account_name}</span>
                      )}
                    </dd>
                  </>
                )}
              </>
            )}
            {result.entity_type === 'leads' && (
              <>
                {result.email && (
                  <>
                    <dt className="text-muted-foreground">Email</dt>
                    <dd className="truncate">{result.email}</dd>
                  </>
                )}
                {result.company && (
                  <>
                    <dt className="text-muted-foreground">Company</dt>
                    <dd>{result.company}</dd>
                  </>
                )}
              </>
            )}
            {result.entity_type === 'opportunities' && (
              <>
                {result.amount && (
                  <>
                    <dt className="text-muted-foreground">Amount</dt>
                    <dd>${parseFloat(result.amount).toLocaleString()}</dd>
                  </>
                )}
                {result.stage_name && (
                  <>
                    <dt className="text-muted-foreground">Stage</dt>
                    <dd>{result.stage_name}</dd>
                  </>
                )}
              </>
            )}
            {result.entity_type === 'tasks' && (
              <>
                {result.status && (
                  <>
                    <dt className="text-muted-foreground">Status</dt>
                    <dd>{result.status}</dd>
                  </>
                )}
                {result.priority && (
                  <>
                    <dt className="text-muted-foreground">Priority</dt>
                    <dd>{result.priority}</dd>
                  </>
                )}
              </>
            )}
            {result.entity_type === 'events' && (
              <>
                {result.activity_date_time && (
                  <>
                    <dt className="text-muted-foreground">Date</dt>
                    <dd>
                      {(
                        toDisplayDate(result.activity_date_time) ??
                        new Date(result.activity_date_time)
                      ).toLocaleDateString()}
                    </dd>
                  </>
                )}
                {result.location && (
                  <>
                    <dt className="text-muted-foreground">Location</dt>
                    <dd>{result.location}</dd>
                  </>
                )}
              </>
            )}
            {(result.entity_type === 'notes' || result.entity_type === 'noteSessions') && (
              <>
                {(result as any).parent_entity_name && (
                  <>
                    <dt className="text-muted-foreground">
                      {result.entity_type === 'noteSessions' ? 'Session' : 'Note'} on
                    </dt>
                    <dd className="col-span-1 flex flex-wrap items-center gap-1">
                      {parentRoute && parentEntityId ? (
                        <Link
                          to={`${parentRoute}/${parentEntityId}`}
                          className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                        >
                          {(result as any).parent_entity_name}
                        </Link>
                      ) : (
                        <span>{(result as any).parent_entity_name}</span>
                      )}
                      {parentEntityType && (
                        <span className="text-xs text-muted-foreground">({parentEntityType})</span>
                      )}
                    </dd>
                  </>
                )}
              </>
            )}
            {/* Owner field — shown for all entity types that have it */}
            {result.owner_name !== undefined && (
              <>
                <dt className="text-muted-foreground">Owner</dt>
                <dd className="flex items-center gap-1">
                  {editingOwner === `${result.entity_type}-${result.id}` ? (
                    <select
                      className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                      defaultValue={result.owner_id || ''}
                      onChange={(e) =>
                        handleOwnerChange(result.entity_type, result.id, e.target.value)
                      }
                      onBlur={() => setEditingOwner(null)}
                      autoFocus
                      aria-label="Select new owner"
                    >
                      <option value="">Select owner...</option>
                      {salesUsers.map((u: any) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <>
                      <span>{result.owner_name || 'Unassigned'}</span>
                      {['accounts', 'contacts', 'leads', 'opportunities'].includes(
                        result.entity_type
                      ) && (
                        <button
                          onClick={() => setEditingOwner(`${result.entity_type}-${result.id}`)}
                          className="text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary rounded"
                          aria-label={`Change owner of ${result.name || result.subject || ''}`}
                        >
                          <Pencil className="h-3 w-3" aria-hidden="true" />
                        </button>
                      )}
                    </>
                  )}
                </dd>
              </>
            )}
          </dl>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Search Results</h1>
          <p className="text-muted-foreground mt-1">
            {totalResults > 0
              ? `Found ${totalResults} result${totalResults !== 1 ? 's' : ''} for "${initialQuery}"`
              : initialQuery
                ? `No results found for "${initialQuery}"`
                : 'Enter a search query to begin'}
          </p>
        </div>
      </div>

      {/* Search form */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <SearchIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                placeholder="Search across all CRM entities..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9"
                aria-label="Search query"
              />
            </div>
            <Button type="submit" disabled={query.trim().length < 2}>
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
          <span className="ml-2">Searching...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Error loading search results. Please try again.</p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {!isLoading && !error && totalResults > 0 && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All Results ({totalResults})</TabsTrigger>
            {entityTypes.map((type) => {
              const Icon = ENTITY_ICONS[type]
              const count = results[type]?.total ?? 0
              return (
                <TabsTrigger key={type} value={type} className="gap-2">
                  {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
                  {ENTITY_LABELS[type]} ({count})
                </TabsTrigger>
              )
            })}
          </TabsList>

          <TabsContent value="all" className="space-y-4 mt-6">
            {entityTypes.map((type) => {
              const entityResults = results[type as keyof typeof results] as EntityResults
              const label = ENTITY_LABELS[type]

              return (
                <div key={type}>
                  <h2 className="text-xl font-semibold mb-3">{label}</h2>
                  <div className="space-y-3">
                    {entityResults.results.slice(0, 5).map(renderResultCard)}
                    {entityResults.hasMore && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setActiveTab(type)}
                      >
                        View all {entityResults.total} {(label ?? '').toLowerCase()}
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </TabsContent>

          {entityTypes.map((type) => {
            const entityResults = results[type as keyof typeof results] as EntityResults

            return (
              <TabsContent key={type} value={type} className="space-y-3 mt-6">
                {entityResults.results.map(renderResultCard)}
                {entityResults.hasMore && (
                  <p className="text-center text-sm text-muted-foreground py-4">
                    Showing {entityResults.results.length} of {entityResults.total} results
                  </p>
                )}
              </TabsContent>
            )
          })}
        </Tabs>
      )}

      {/* Empty state */}
      {!isLoading && !error && totalResults === 0 && initialQuery && (
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <SearchIcon
              className="h-12 w-12 text-muted-foreground mx-auto mb-4"
              aria-hidden="true"
            />
            <h3 className="text-lg font-semibold mb-2">No results found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search query or search for different terms.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
